import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const FinancialStressDistribution = ({ data }) => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'operations', label: 'Operations' },
    { value: 'hr', label: 'Human Resources' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-xs text-muted-foreground">
              {entry?.name}: {entry?.value} employees
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2 border border-border">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4 md:mb-6">
        <div className="flex-1">
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1 md:mb-2">
            Financial Stress Distribution
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Department-level stress analytics with trend analysis
          </p>
        </div>
        <div className="w-full lg:w-64">
          <Select
            options={departmentOptions}
            value={selectedDepartment}
            onChange={setSelectedDepartment}
            placeholder="Filter by department"
          />
        </div>
      </div>

      <div className="w-full h-64 md:h-72 lg:h-80" aria-label="Financial Stress Distribution Bar Chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '12px' }}
              iconType="circle"
            />
            <Bar dataKey="low" fill="var(--color-success)" name="Low Stress" radius={[4, 4, 0, 0]} />
            <Bar dataKey="medium" fill="var(--color-warning)" name="Medium Stress" radius={[4, 4, 0, 0]} />
            <Bar dataKey="high" fill="var(--color-error)" name="High Stress" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 md:p-3 rounded-lg bg-success/10">
            <Icon name="TrendingDown" size={20} className="text-success" />
          </div>
          <div>
            <p className="text-xs md:text-sm text-muted-foreground">Avg. Stress Level</p>
            <p className="text-base md:text-lg font-semibold text-foreground">3.2/10</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 md:p-3 rounded-lg bg-warning/10">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
          </div>
          <div>
            <p className="text-xs md:text-sm text-muted-foreground">At-Risk Employees</p>
            <p className="text-base md:text-lg font-semibold text-foreground">187</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 md:p-3 rounded-lg bg-primary/10">
            <Icon name="Users" size={20} className="text-primary" />
          </div>
          <div>
            <p className="text-xs md:text-sm text-muted-foreground">Improved This Month</p>
            <p className="text-base md:text-lg font-semibold text-foreground">94</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialStressDistribution;